import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import Icon from '../../../../components/atoms/Icon'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import { useAttachment } from '../contexts/AttachmentContext'
import { ATTACHMENT_TYPES } from '../utils/attachmentUtils'
import StatusBadge from './StatusBadge'

const useStyles = makeStyles((theme) => ({
  toolCall: {
    marginTop: 'var(--margin-sm)',
    marginBottom: 'var(--margin-xs)',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  toolHeader: {
    padding: 'var(--padding-sm) 0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'background-color 0.2s ease'
  },
  toolHeaderClickable: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8
    }
  },
  toolHeaderPending: {
    cursor: 'default',
    opacity: 0.7
  },
  expandIcon: {
    fontSize: '16px',
    transition: 'transform 0.2s ease',
    color: 'var(--dai-color-text)'
  },
  expandIconRotated: {
    transform: 'rotate(90deg)'
  },
  toolIcon: {
    fontSize: '20px',
    color: 'var(--color-info-500)'
  },
  toolName: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-bold)',
    flex: 1,
    color: 'var(--dai-color-text)'
  },
  toolStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  duration: {
    fontSize: 'var(--font-size-xs)'
  },
  toolDetails: {
    padding: 'var(--padding-sm)',
    borderTop: 'var(--dai-border1)'
  },
  section: {
    marginBottom: 'var(--margin-sm)',
    '&:last-child': {
      marginBottom: 0
    }
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    marginBottom: '8px',
    textTransform: 'uppercase'
  },
  codeBlock: {
    backgroundColor: 'var(--dai-color-input)',
    color: 'var(--dai-contrast-input)',
    padding: 'var(--padding-sm)',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: 'var(--font-family-monospace)',
    overflow: 'auto',
    maxHeight: '300px',
    border: 'var(--dai-border1)',
    lineHeight: 1.5
  },
  errorText: {
    color: 'var(--color-danger-500)',
    fontSize: 'var(--font-size-sm)'
  },
  loadingIndicator: {
    animation: '$rotate 1s linear infinite'
  },
  '@keyframes rotate': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  }
}))

function ToolCallVisualizer ({ parentInfo, messageId, name, toolCallId, status, subType }) {
  const classes = useStyles()
  const { openAttachment } = useAttachment()

  const handleClick = () => {
    // Only open panel if tool call is in terminal state (not pending)
    if (status === 'pending') {
      return
    }

    openAttachment({
      attachmentId: `tool-call-${toolCallId}`,
      title: 'Tool Details',
      contentType: ATTACHMENT_TYPES.TOOL_CALL,
      inline: true,
      data: {
        toolCallId,
        parentInfo,
        messageId,
        subType
      }
    })
  }

  const isClickable = status !== 'pending'
  const headerClass = `${classes.toolHeader} ${isClickable ? classes.toolHeaderClickable : classes.toolHeaderPending}`

  return (
    <FadeIn className='font-sans radius-sm'>
      <div
        className={headerClass}
        onClick={handleClick}
      >
        <Icon
          name='build'
          style={{
            fontSize: '14px',
            animation: status === 'pending' ? 'rotate 1s linear infinite' : 'none'
          }}
        />
        <span className={classes.toolName}>{name}</span>
        <div className={classes.toolStatus}>
          <StatusBadge status={status} />
        </div>
      </div>
    </FadeIn>
  )
}

ToolCallVisualizer.propTypes = {
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  }),
  messageId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['success', 'error', 'pending']),
  toolCallId: PropTypes.string.isRequired,
  subType: PropTypes.string
}

export default ToolCallVisualizer
